import React from "react";
import { View, Text, StyleSheet } from "react-native";
import { Button, useTheme } from "react-native-paper";
import Svg, { Path } from "react-native-svg";

import { useTutorial } from "../context/TutorialContext";
import type { SegColor, Segment } from "../game/tutorialGuidance";
import type { ExtendedTheme } from "../theme/SynapseTheme";
import { withAlpha } from "../utils/color";
import { spiralPath } from "./board/markGeometry";
import CustomIcon from "./CustomIcon";

// The same spiral as the header mark (AriadneHeaderMark), drawn small and still.
const MARK_R = 10;
const MARK_VIEW_BOX = "-10 -10 28.5 20";

/**
 * First-run guidance callout, in Ariadne's voice on the first and last cards
 * (owner, 2026-10-21); her mark sits before the headline on the first. Renders as a bubble with Skip/Done tucked
 * into its top-right corner and colored text segments — with inline icons for gestures (tap,
 * long-press, right-click) and the backtrack action. The mode badge above the
 * board already says "Tutorial"; the bubble does not repeat it.
 * No-ops when the walkthrough is inactive.
 */
const TutorialBanner: React.FC = () => {
  const { walkthroughActive, guidance, skip } = useTutorial();
  const { customColors, colors } = useTheme() as ExtendedTheme;

  // The hint blue is the board's current-node colour, shared with the chip
  // highlight. The banner itself stays still; the hinted chip carries the glow.
  const accent = customColors.currentNode;

  if (!walkthroughActive || !guidance) return null;

  const colorFor = (c: SegColor | undefined): string => {
    switch (c) {
      case "gold":
        return customColors.globalOptimalNode;
      case "purple":
        return customColors.localOptimalNode;
      case "red":
        return customColors.endNode;
      case "blue":
        return customColors.currentNode;
      case "green":
        return customColors.startNode;
      default:
        return colors.onSurface;
    }
  };

  const renderSegments = (segs: Segment[], iconSize: number) =>
    segs.map((s, i) =>
      s.icon ? (
        <CustomIcon
          key={i}
          source={s.icon}
          size={iconSize}
          color={colorFor(s.color)}
        />
      ) : (
        <Text key={i} style={{ color: colorFor(s.color) }}>
          {s.text}
        </Text>
      ),
    );

  const headline = guidance.headline ?? [];
  const body = guidance.headline ? (guidance.body ?? []) : guidance.segments;

  return (
    <View style={styles.wrap} testID="tutorial-banner">
      <View
        style={[
          styles.bubble,
          { borderColor: accent, backgroundColor: withAlpha(accent, 0.12) },
        ]}
      >
        {headline.length > 0 && (
          <View style={styles.headlineRow}>
            {guidance.ariadne && (
              <View style={styles.mark} testID="tutorial-ariadne-mark">
                <Svg width={26} height={18} viewBox={MARK_VIEW_BOX}>
                  <Path
                    d={spiralPath(MARK_R)}
                    fill="none"
                    stroke={customColors.aiPath}
                    strokeWidth={1.8}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </Svg>
              </View>
            )}
            <Text
              style={[
                styles.headline,
                styles.clearCorner,
                { color: colors.onSurface },
              ]}
              testID="tutorial-headline"
            >
              {renderSegments(headline, 16)}
            </Text>
          </View>
        )}
        {body.length > 0 && (
          <Text
            style={[
              styles.message,
              headline.length === 0 && styles.clearCorner,
              { color: colors.onSurface },
            ]}
            testID="tutorial-body"
          >
            {renderSegments(body, 14)}
          </Text>
        )}
        <Button
          compact
          mode="text"
          onPress={skip}
          testID="tutorial-skip"
          style={styles.skip}
        >
          {guidance.done ? "Done" : "Skip"}
        </Button>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    paddingHorizontal: 12,
    marginBottom: 4,
    maxWidth: 500,
    alignSelf: "center",
    width: "100%",
  },
  bubble: {
    flexDirection: "column",
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
  },
  // Sits in the corner over the first line; that line keeps the room.
  skip: {
    position: "absolute",
    top: 3,
    right: 6,
  },
  clearCorner: { paddingRight: 48 },
  headlineRow: { flexDirection: "row", alignItems: "center", gap: 6 },
  mark: { height: 21, justifyContent: "center" },
  headline: { fontSize: 15, lineHeight: 21, fontWeight: "700", flexShrink: 1 },
  message: { fontSize: 13, lineHeight: 19, marginTop: 2 },
});

export default TutorialBanner;
